import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';

import type { Role } from '../../constants/rbac';

interface AuthState {
  authProvider: 'firebase' | null;
  firebaseUid: string | null;
  authInitialized: boolean;
  isAuthenticated: boolean;
  // TRUE only after the backend session cookie has actually been minted
  // (FirebaseAuthSessionBridge, after syncBackendSession resolves). DISTINCT
  // from authInitialized: that one is set in the bridge's `finally` and goes
  // true even when the session sync throws, so it cannot gate a cookie-authed
  // request. This one gates the homepage's cookie-only fetches (collections,
  // trending) so they never fire before the __session cookie exists. Never
  // persisted across a reload - see the REHYDRATE reset below.
  sessionReady: boolean;
  expiresAt: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  // Whether Firebase says the email address is confirmed (27 Oct, step 7). It shows on the waiting page and, since 29 Oct,
  // it also chooses which page / shows to an account with no role (components/AuthGuards/HomeEntry: the waiting homepage
  // when confirmed, else the waiting page). It grants nothing: the gateway still decides every access (the waiting
  // preview checks email_verified in the verified token; verification is checked when a role is granted).
  emailVerified: boolean;
  // RBAC claims from the verified Firebase ID token (FirebaseAuthSessionBridge
  // -> getIdTokenResult().claims). FAIL CLOSED: null unless the token carried
  // one of the seven known role strings. null role = no access to any
  // RBAC-gated surface. Never written from anything the client invents - the
  // only writer is the bridge, reading the signed token.
  role: Role | null;
  tenantId: string | null;
}

const initialState: AuthState = {
  authProvider: null,
  firebaseUid: null,
  authInitialized: false,
  isAuthenticated: false,
  sessionReady: false,
  expiresAt: null,
  firstName: null,
  lastName: null,
  email: null,
  emailVerified: false,
  role: null,
  tenantId: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess: (
      state,
      action: PayloadAction<{
        expiresAt: string;
        email?: string | null;
        emailVerified?: boolean;
        uid?: string | null;
        role?: Role | null;
        tenantId?: string | null;
      }>
    ) => {
      state.authProvider = 'firebase';
      state.firebaseUid = action.payload.uid || state.firebaseUid;
      state.authInitialized = true;
      state.isAuthenticated = true;
      state.expiresAt = action.payload.expiresAt;
      state.email = action.payload.email || state.email;
      state.emailVerified = action.payload.emailVerified === true;
      // ABSENT MEANS NULL, deliberately - not "keep the previous value". A
      // login event that carries no validated claims (the legacy sign-up and
      // reset forms dispatch only expiresAt) must clear any role rather than
      // let a stale one survive into the new session. Fail closed.
      state.role = action.payload.role ?? null;
      state.tenantId = action.payload.tenantId ?? null;
    },
    setAuthInitialized: (state, action: PayloadAction<boolean>) => {
      state.authInitialized = action.payload;
    },
    // Set true ONLY after syncBackendSession resolves (the bridge). The
    // homepage fetch hooks gate on it. Nothing else writes it.
    setSessionReady: (state, action: PayloadAction<boolean>) => {
      state.sessionReady = action.payload;
    },
    logout: (state) => {
      state.authProvider = null;
      state.firebaseUid = null;
      state.authInitialized = true;
      state.isAuthenticated = false;
      state.sessionReady = false;
      state.expiresAt = null;
      state.email = null;
      state.emailVerified = false;
      state.firstName = null;
      state.lastName = null;
      // The existing sign-out clear path - RBAC claims go with the session.
      state.role = null;
      state.tenantId = null;
    },
  },
  extraReducers: (builder) => {
    // sessionReady must NEVER survive a reload. Since 28 Oct (item 3) the store's
    // persist transform (store/index.ts, authFlagsNotRestored) never saves it and
    // restores it false; this case alone could not, because redux-persist keeps the
    // saved slice whenever this reducer leaves it unchanged. The auth slice is saved, so a
    // rehydrated `sessionReady: true` would re-open the exact first-load race
    // the flag closes (the __session cookie it stands for does not persist the
    // same way). Force it false on every rehydrate, independent of the
    // persisted payload. Kept as a second guard.
    builder.addCase(REHYDRATE, (state) => {
      state.sessionReady = false;
    });
  },
});

export const { loginSuccess, logout, setAuthInitialized, setSessionReady } = authSlice.actions;

export default authSlice.reducer;
